import { trigger, state, style, transition, animate } from '@angular/animations';
import { BreakpointObserver } from '@angular/cdk/layout';
import { HttpClient } from '@angular/common/http';
import { Component, HostListener, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { forkJoin, from, Observable, of, Subject } from 'rxjs';
import { catchError, filter, first, map, takeUntil } from 'rxjs/operators';
import { FirebaseService } from 'src/app/firebase.service';
import { TourService } from '../tour.service';
import { ResponsiveBreakpoints } from 'src/app/matches.directive';
import * as en_stop from '../stops';
import * as es_stop from '../stops.es';
import { TourMapComponent } from './components/tour-map/tour-map.component';

@Component({
  selector: 'app-tour-stop-map',
  templateUrl: './tour-stop-map.component.html',
  styleUrls: ['./tour-stop-map.component.scss'],
  animations: [
    trigger('arrivedTrigger', [
      state('true', style({ "background-position-x": "100%", color: "white"})),
      state('false', style({ "background-position-x": "0%", color: "#ACA8A8" })),
      transition('true <=> false', animate('.5s ease'))
    ]),
    trigger('infoAnimate', [
      transition(':leave', [
        style({'max-height': "1000vh", height: "600vh"}),
        animate('1s ease', style({'max-height': '0px', height: "0"}))
      ])
    ]),
    trigger('arrived', [
      transition(':leave', [
        animate('.8s ease', style({ transform: 'translateY(-150%)' }))
      ])
    ]),
    trigger('finished', [
      transition(':enter', [
        style({ opacity: 0 }),
        animate('.2s ease', style({ opacity: 1 }))
      ])
    ])
  ],
})
export class TourStopMapComponent implements OnInit, OnDestroy {
  showLoadingTour = true;
  tourInfoLoaded = false;
  stopInfos = [];
  audiosByStop = [];
  destroyed$ = new Subject<boolean>();
  stopNumber: number = 0;
  isDesktopDevice = false;
  stopSlug = '';
  oldSlug = '';
  slugs = [];
  fetchingLocation = false;
  locationAllowed = true;
  stopCoordinates = [];
  showWelcomeBox = false;
  arrivedAtStop = false;
  showStopInfo = false;
  language = 'en';
  infos = [];
  narrations = [];
  displayMenu = false;
  state = 'begin';
  banner: any;
  stopPaths = [];
  overflowHidden = false;
  audioHidden = false;
  tourFinished = false;
  toursInfoLoaded: Record<number, boolean> = {};

  @ViewChild(TourMapComponent) tourMapComp: TourMapComponent;

  @HostListener('window:scroll', ['$event'])
  scrollHandler(event) {
    const ratio = window.innerHeight*.16;
    if (window.scrollY+window.innerHeight >= window.document.body.scrollHeight - 50) {
      this.overflowHidden = true;
      this.audioHidden = true;
    } else {
      this.overflowHidden = false;
      this.audioHidden = false;
    }
  }

  constructor(
    private router: Router,
    public breakpointObserver: BreakpointObserver,
    private activeRoute: ActivatedRoute,
    private httpClient: HttpClient,
    private fs: FirebaseService,
    public tourService: TourService
  ) {
    this.setupBreakPointListener();
    this.setupRouteListeners();
  }

  ngOnInit(): void {}

  setupBreakPointListener() {
    this.breakpointObserver
      .observe([ResponsiveBreakpoints.desktop])
      .pipe(takeUntil(this.destroyed$))
      .subscribe((result) => {
        this.isDesktopDevice = result.matches;
      });
  }

  /**
   * @description Used to
   * load stop information based on user's language
   * (english OR spanish)
   */
  setupTourData() {
    let isSpanish =
      this.router.url.slice(0, 4) == '/es' ||
      this.router.url.slice(0, 4) == '/es/';
    this.language = isSpanish ? 'es' : 'en';
    this.infos = isSpanish ? es_stop.INFOS : en_stop.INFOS;
    this.narrations = isSpanish ? es_stop.NARRATIONS : en_stop.NARRATIONS;
    this.banner = isSpanish ? es_stop.BANNER : en_stop.BANNER;
    this.slugs = isSpanish ? es_stop.SLUGS : en_stop.SLUGS;
    if (isSpanish) {
      this.stopInfos = en_stop.INFOS.slice();
      this.audiosByStop = en_stop.NARRATIONS.slice();
    } else {
      this.stopInfos = es_stop.INFOS.slice();
      this.audiosByStop = es_stop.NARRATIONS.slice();
    }
  }

  handleTourLoad() {
    this.toursInfoLoaded[this.stopNumber] = true;
    this.tourInfoLoaded = true;
  }

  showTourInfo() {
    this.showStopInfo = true;
    this.arrivedAtStop = false;
    if (!this.stopCoordinates.length) {
      this.handleDownloadTour();
    } else {
      this.loadFirebaseData().subscribe(
        ([response, paths]) => {
          this.stopCoordinates = response;
          this.stopPaths = paths;
          this.handleDownloadTour()
        },
        error => {
          console.log('error',error);
          alert('Failed to load stop coordinates. Please try again')
        }
      )
    }
  }

  handleDownloadTour() {
    if (!this.toursInfoLoaded[this.stopNumber]) {
      this.downloadTourInfo()
      .pipe(
        takeUntil(this.destroyed$),
      ).subscribe(
        (response) => {
          this.handleTourLoad();
        },
        (error) => {
          this.handleTourLoad();
        }
      );
    } else {
      this.showLoadingTour = false;
      this.tourInfoLoaded = true;
      this.fetchingLocation = false;
      // this.showStopInfo = false;
    }
  }

  /**
   * @description Used to
   * get changing tour stop slug name
   */
  setupRouteListeners() {
    // this.activeRoute.params
    this.router.events
      .pipe(
        filter(event => event instanceof NavigationEnd),
        takeUntil(this.destroyed$)
      )
      .subscribe((params) => {
        console.log('params', params, this.stopSlug);
        const fragment = this.router.parseUrl(this.router.url).fragment;
        if (!fragment || fragment !== 'iOSInstructions') {
          // this.stopSlug = params['stopSlug'];
          const isSpanish =
          this.router.url.slice(0, 4) == '/es' || this.router.url.slice(0, 4) == '/es/';
          const stopUrl = isSpanish ? this.router.url.slice(4) : this.router.url.slice(1);
          const stopSlug = stopUrl.slice(-5) == '/info' ? stopUrl.slice(0, -5) : stopUrl;
          if (this.stopSlug) {
            this.oldSlug = this.stopSlug;
          }
          this.stopSlug = stopSlug;
          console.log('stopSlug', this.stopSlug, this.oldSlug);
          this.setupTourData();
          this.findStop();
          // if info page
          // check if need to load data else directly show the info page
          if (stopUrl.slice(-5) == '/info') {
            this.showTourInfo();
          } else {
            this.showStopInfo = false;
            this.loadFirebaseData().subscribe(
              ([response, paths]) => {
                this.stopCoordinates = response;
                this.stopPaths = paths;
                this.handleDownloadTour()
              },
              error => {
                console.log('error',error);
                alert('Failed to load stop coordinates. Please try again')
              }
            )
          }
        }
      });
  }

  downloadTourInfo() {
    this.showLoadingTour = true;
    this.tourInfoLoaded = false;
    this.fetchingLocation = false;
    const stopImageData = (
      this.stopInfos[this.stopNumber] as Array<Record<string, any>>
    ).filter((obj) => obj.img);
    const imagesToDownload = stopImageData.map((imgObj) => {
      return this.isDesktopDevice
        ? imgObj.img.desktopUrl
        : imgObj.img.mobileUrl;
    });
    imagesToDownload.push(
      `../../assets/banners/${
        this.isDesktopDevice ? 'desktop' : 'mobile'
      }/banner${this.stopNumber + 1}.png`
    );
    let images1ToDownload = [];
    const audiosToDownload = [];
    // const audio = this.audiosByStop[this.stopNumber];
    // if (audio) {
    //   audiosToDownload.push(audio);
    // }
    console.log('dataToDownload', imagesToDownload, audiosToDownload);
    if (imagesToDownload.length || audiosToDownload.length) {
      let observablesArray = imagesToDownload.map((url) =>
        this.httpClient.get(url, { responseType: 'blob' }).pipe(
          catchError((error) => {
            console.log('error', error);
            return of(true);
          })
        )
      );

      const audioObservables = audiosToDownload.map((audioLink) => {
        const audioEl = new Audio();
        audioEl.src = audioLink;
        audioEl.preload = 'auto';
        const audioPromise = new Promise((resolve, rej) =>
          audioEl.addEventListener('canplaythrough', () => resolve(true), false)
        );
        return from(audioPromise) as Observable<boolean>;
      });

      return forkJoin(observablesArray.concat(audioObservables));
    } else {
      return of([]);
    }
  }

  findStop() {
    this.stopNumber = en_stop.SLUGS.indexOf(this.stopSlug.toLowerCase());
  }

  loadFirebaseData() {
    return forkJoin([
      this.loadTourStopCoordinates(),
      this.loadPaths(),
    ])
  }

  loadTourStopCoordinates() {
    let points = 'points';
    if (this.router.url == '/test') {
      console.log('TEST POINTS');
      points = 'test-points';
      // this.testing = true;
    }

    return this.fs.getAllPoints(points).pipe(
      first(),
      map((data) => {
        const result = [];
        data.forEach((docSnap) => {
          result.push(docSnap.data());
        });
        console.log('result', result);
        return result;
      })
    );
  }

  loadPaths() {
    return this.fs
    .getPaths()
    .pipe(
      first(),
      map((data) => {
        const result = [];
        data.forEach((snap) => {
          const snapData = snap.payload.doc.data();
          result.push(snapData.coordinates);
        });
        console.log('result', result);
        return result;
      })
    )
  }

  handleContinue() {
    this.showLoadingTour = false;
    if (!this.showStopInfo) {
      this.fetchingLocation = true;
    }
  }

  onMapLoad() {
    if (!this.tourService.fetchedLocation) {
      this.tourMapComp.locateUser();
    } else {
      this.showMap();
    }
  }

  handleTrackedLocation($event: boolean) {
    this.locationAllowed = $event;
    if (this.locationAllowed) {
      this.showMap();
    }
  }

  showMap() {
    this.fetchingLocation = false;
    this.tourService.setFetchedLocation();
    this.showWelcomeBox = true;
    // need to use set-timeout to make sure
    // resize happens after map is visible
    setTimeout(() => {
      this.tourMapComp.mapInstance.resize();
    }, 100);
    if (this.stopNumber < 10) {
      // if (this.stopNumber > 0) {
      //   this.tourMapComp.drawPointsWithLine();
      // } else {
      //   this.tourMapComp.drawStartingPoint();
      // }
      this.tourMapComp.drawStartingPoint();
    }
  }

  continueWithoutGPS() {
    this.showMap();
  }

  closeWelcomeToMapBox() {
    this.showWelcomeBox = false;
  }

  handleArrived() {
    this.arrivedAtStop = true;
  }

  goToInfoPage() {
    this.router.navigate(['info'], {
       relativeTo: this.activeRoute
    })
  }

  goToNextStop() {
    this.stopNumber++;
    if (this.stopNumber === this.slugs.length) {
      this.tourFinished = true;
    } else {
      this.router.navigate([this.slugs[this.stopNumber]])
    }
  }

  toggleMenu(state) {
    this.displayMenu = state;
  }


  ngOnDestroy(): void {
    console.log('destroy');
    this.destroyed$.next(true);
    this.destroyed$.complete();
  }
}
